import queryString from 'query-string';
import { FC, ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { combineReducers } from 'redux';
import useReducerWithThunk from 'use-reducer-thunk';
import { Segment } from '~src/interfaces/segment.interface';

import { SurveySubjectTypes } from '~src/interfaces/surveys.interfaces';
import { Topic } from '~src/interfaces/topic.interface';
import { Uuid } from '~src/services/uuid';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import { OptionItem } from '~src/shared/selectors/DdSearchContent';
import { getUrlFromObject } from '~src/utils';
import { GlobalSpinnerContainer } from '~src/modules/heatmap/components/table/ratingTable.sc';

import { dataReducer, initialDataState } from '~src/modules/spidergraph/data.reducer';
import RecommendationsFilterDetails from '~src/modules/recommendations/components/ai/RecommendationsFilterDetails';
import {
  deselectTopicAction,
  getAttributesAndScales,
  navigationReducer,
  selectCyclesAction,
  selectAboutSegmentsAction,
  selectSurveyAction,
  selectSurveyTypeAction,
  selectTopicAction,
} from '~src/modules/spidergraph/navigation.reducer';
import SurveySelector from '~src/modules/spidergraph/components/SurveySelector';
import { AnalysisSpiderParams, RichCycle } from '~src/modules/spidergraph/types';
import LightTwoColsHeader from '~src/shared/headers/lightTwoColsHeader/LightTwoColsHeader';
import { TilesView } from '~src/shared/tilesView/TilesView';
import { StoreI } from '~src/rootReducer';
import EmptyListMessage from '~src/modules/reports/shared/components/EmptyListMessage';
import { Container, FilterContainer } from './components/ai/recommendationsFilterDetails.sc';

interface OwnProps {
  topicId?: string;
  onUrlParamsChange?: (oldParams: AnalysisSpiderParams, newParams: AnalysisSpiderParams) => void;
}

interface RecommendationsInterface {
  text: string;
}

interface RecommendationsListProps {
  recommendation?: RecommendationsInterface;
}

export type AnalysisProps = RouteComponentProps<never> & OwnProps;

const isUUID = (uuid: string): boolean => Boolean(Uuid.fromHexStringOrUndefined(uuid));

const Recommendations: FC<AnalysisProps> = (props) => {
  const initialNavigationState = queryString.parse(props.location.search) as AnalysisSpiderParams;
  initialNavigationState.isSelfAssessmentIncluded =
    String(initialNavigationState.isSelfAssessmentIncluded) === 'true';
  const [localState, localDispatch] = useReducerWithThunk(
    combineReducers({
      data: dataReducer,
      navigation: navigationReducer,
    }),
    {
      data: initialDataState(),
      navigation: initialNavigationState,
    },
  );

  // redundant, but helps the IDE
  const navState = localState!.navigation;
  const dataState = localState!.data;

  const [recommendation, isLoadingRecommendation] = useSelector(({ spidergraph }: StoreI) => {
    return [
      spidergraph.recommendations && spidergraph.recommendations.length
        ? spidergraph.recommendations[0]
        : undefined,
      spidergraph.isLoadingRecommendations,
    ];
  });
  const [, setSelectedSegments] = useState<Segment[]>([]);
  const [selectedCycles, setSelectedCycles] = useState<RichCycle[]>([]);
  const [autoSelectMostRecentCycle, setAutoSelectMostRecentCycle] = useState<boolean>(false);

  const userCompanyId = useSelector<StoreI, string | undefined>((s) => {
    return s.user.session.company?.uuid;
  });

  useEffect(() => {
    localDispatch(getAttributesAndScales());
    if (navState?.survey && navState?.surveySubjectType) {
      localDispatch(selectSurveyAction(navState.survey, navState.surveySubjectType));
    }
    if (navState?.aboutSegments) {
      localDispatch(selectAboutSegmentsAction(navState.aboutSegments));
    }
    if (navState?.cycles && navState?.cycles.length) {
      localDispatch(selectCyclesAction(navState.cycles));
    } else {
      setAutoSelectMostRecentCycle(true);
    }
    if (navState?.topic) {
      localDispatch(selectTopicAction(navState.topic));
    }
  }, []);

  useEffect(() => {
    // on load, auto-select single topics
    if (!navState?.topic && dataState?.topics && dataState.topics.length === 1) {
      localDispatch(selectTopicAction(dataState.topics[0].id));
    }
  }, [dataState?.topics]);

  useEffect(() => {
    // reset segments, for example on survey change
    if (navState?.aboutSegments && !navState.aboutSegments.length) {
      setSelectedSegments([]);
    }
  }, [navState?.aboutSegments]);

  useEffect(() => {
    if (autoSelectMostRecentCycle && !(navState?.cycles || []).length) {
      const mostRecentCycle = RichCycle.getMostRecentCycle(dataState.richCycles);
      if (mostRecentCycle) {
        setAutoSelectMostRecentCycle(false);
        localDispatch(selectCyclesAction([mostRecentCycle.getId()]));
        return;
      }
    }

    setSelectedCycles(dataState.richCycles.filter(r => (navState?.cycles || []).indexOf(r.getId()) !== -1));
  }, [navState?.cycles, dataState.richCycles]);

  const onSurveySelectChange = (selectedItem: OptionItem, type: SurveySubjectTypes) => {
    setAutoSelectMostRecentCycle(true);
    if (isUUID(selectedItem.id)) {
      localDispatch(selectSurveyAction(selectedItem.id, type));
    } else {
      localDispatch(selectSurveyTypeAction(type));
    }
  };

  useEffect(() => {
    props.history.replace({ search: getUrlFromObject(navState) });
  }, [navState]);

  const loadingSurveyRelatedData =
    dataState.loadingAttrs || dataState.loadingScales || dataState.loadingSurvey;

  const onSegmentsSelectionChange = (segments: Segment[]) => {
    localDispatch(selectAboutSegmentsAction(segments.map(s => s.id)));
    setSelectedSegments(segments);
  };

  const onCyclesSelectionChange = (cycles: RichCycle[]) =>
    localDispatch(selectCyclesAction(cycles.map(c => c.getId())));

  const onTopicSelectionChange = (topic?: Topic) => {
    localDispatch(topic ? selectTopicAction(topic.id) : deselectTopicAction());
  };

  const chartControlsDataAvailable =
    dataState.richCycles.length > 0 && navState.survey === dataState.richCycles[0].survey.id;

  const defaultAboutSegmentIds = userCompanyId ? [userCompanyId] : [];

  return (
    <Container>
      {loadingSurveyRelatedData ? (
        <GlobalSpinnerContainer>
          <LoadingSpinner />
        </GlobalSpinnerContainer>
      ) : (
        <>
          <LightTwoColsHeader
            title={{ key: 'x.ai.header.title' }}
            descr={{ key: 'x.ai.header.description' }}
          />
          <SurveySelector
            urlParams={navState}
            activeSelect={navState.surveySubjectType}
            activeSurveyId={navState.survey}
            onSurveyChange={onSurveySelectChange}
            hidePeopleSelector
            isLoading={Boolean(isLoadingRecommendation)}
          />
          <FilterContainer>
            {chartControlsDataAvailable ? (
              <RecommendationsFilterDetails
                availableCycles={dataState.richCycles}
                availableTopics={Object.values(dataState.topics || {})}
                selectedCycles={selectedCycles}
                selectedSegmentsUuids={
                  navState?.aboutSegments?.length ? navState.aboutSegments : defaultAboutSegmentIds
                }
                selectedTopic={navState.topic}
                onCyclesChange={onCyclesSelectionChange}
                onSegmentsChange={onSegmentsSelectionChange}
                onTopicChange={onTopicSelectionChange}
              />
            ) : null}
          </FilterContainer>
          {!isLoadingRecommendation ? <RecommendationView recommendation={recommendation} /> : null}
        </>
      )}
    </Container>
  );
};

const RecommendationView: FC<RecommendationsListProps> = ({ recommendation }): ReactElement => {
  const [t] = useTranslation();

  if (!recommendation) {
    return (
      <EmptyListMessage
        marginTop={50}
        message={{
          txt: t('x.ai.data_not_found'),
          key: 'x.ai.data_not_found',
        }}
      />
    );
  }

  return <TilesView text={recommendation.text} />;
};
export default Recommendations;
